import { defineStore } from 'pinia';

import type { User } from '@stuut/api/models';
import { waitFor } from '@stuut/common/src/utils';

import { stuut } from '@/api';

interface State {
  loading: boolean;
  users: User[];
}

export const useUsers = defineStore('users', {
  actions: {
    async fetchUsers(force = false) {
      try {
        if (!force && (this.$state.users.length || this.$state.loading)) {
          await waitFor(() => !this.$state.loading, 250);
        } else {
          this.$patch({ loading: true });

          // Fetch all pages from v1/users
          const allUsers: User[] = [];
          let currentPage = 0;
          let hasMorePages = true;

          while (hasMorePages) {
            const result = await stuut.users.list(
              { params: { limit: 100, page: currentPage } },
              true, // Return paginated result with page info
            );

            allUsers.push(...result.data);

            // Check if there's a next page. Guard against a non-advancing
            // next_page so a backend regression can't cause an infinite loop.
            if (result.next_page && result.next_page !== currentPage) {
              currentPage = result.next_page;
            } else {
              hasMorePages = false;
            }
          }

          // Sort users by name
          allUsers.sort((a, b) => (a.name < b.name ? -1 : 1));
          this.$patch({ loading: false, users: allUsers });
        }

        return this.$state.users;
      } catch (error: any) {
        console.error(error);
        this.$patch({ loading: false });
        throw error;
      }
    },
  },
  getters: {
    getUserById: (state) => {
      return (userId: string) => state.users.find((user) => user.id === userId);
    },
  },
  state: (): State => ({
    loading: false,
    users: [],
  }),
});

export default useUsers;
